import { cva, type VariantProps } from 'class-variance-authority'
import { Label as AriaLabel, type LabelProps as AriaLabelProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Label}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm, md (default), lg
 * - Required indicator (*) shown when isRequired prop is true
 * - Disabled state reduces opacity
 *
 * @example
 * ```tsx
 * const classes = labelVariants({ size: 'md' })
 * // Returns: "text-ds-base font-medium text-ds-text ..."
 * ```
 */
export const labelVariants = cva('font-normal text-ds-text select-none', {
	variants: {
		size: {
			sm: 'text-ds-sm',
			md: 'text-ds-base',
			lg: 'text-ds-lg',
		},
	},
	defaultVariants: { size: 'md' },
})

/**
 * Props for the {@link Label} component.
 *
 * Extends React Aria's LabelProps with CVA size variants and required indicator.
 *
 * @property size - Label size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property children - Label text
 * @property isRequired - Show required indicator (*)
 * @property isDisabled - Disabled state (reduced opacity)
 *
 * @example
 * ```tsx
 * <Label size="md" isRequired>
 *   Email address
 * </Label>
 * ```
 */
export interface LabelProps extends Omit<AriaLabelProps, 'className'>, VariantProps<typeof labelVariants> {
	className?: string
	isRequired?: boolean
	isDisabled?: boolean
}

/**
 * Accessible form label primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Proper label-input association via React Aria's `<Label>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small, medium (default), large text sizes.
 * - **Required indicator:** Red asterisk (*) shown when `isRequired={true}`.
 * - **Disabled state:** Reduced opacity when `isDisabled={true}`.
 *
 * @example Basic usage
 * ```tsx
 * import { Label } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <div>
 *       <Label htmlFor="email">Email address</Label>
 *       <Input id="email" type="email" />
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With required indicator
 * ```tsx
 * <Label htmlFor="password" isRequired>
 *   Password
 * </Label>
 * <Input id="password" type="password" />
 * ```
 *
 * @example With disabled state
 * ```tsx
 * <Label htmlFor="disabled-field" isDisabled>
 *   Disabled field
 * </Label>
 * <Input id="disabled-field" isDisabled />
 * ```
 *
 * @see {@link labelVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/label/label.stories.tsx for visual examples
 */
export function Label({ size, className, children, isRequired, isDisabled, ...props }: LabelProps) {
	return (
		<AriaLabel
			{...props}
			className={cn(labelVariants({ size }), isDisabled && 'opacity-50 cursor-not-allowed', className)}
		>
			{children}
			{isRequired && <span className="text-ds-danger ml-1">*</span>}
		</AriaLabel>
	)
}
